import React, { Fragment, useCallback, useMemo, useState } from 'react';
import {
    AnswerType,
    BadgeTypes,
    GroupSpecificity,
    IdealQuestionSurveyResult,
    QuestionResultToView,
    ResultType,
} from '@sapphicsavvy/business';
import { useSignedInContext } from '../../../../context/SignedInContext';
import { mbGetMediumImage } from '@mightybyte/rnw.components.image';
import Layout from '../components/Layout';
import { Image, Platform, StyleSheet, Text, View } from 'react-native';
import { mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { FONT_STYLES, textStyles } from '../../../../constants/textStyles';
import SliderResultCard from '../components/SliderResultCard';
import { Spacer } from '../../../helperComponents/Spacer';
import { imageUtils } from '../../../../utils/imageUtils';
import { capitalize, getNumberOfSteps } from '../../../../utils/shared';
import MostIdealBadgeModal from '../modals/MostIdealBadgeModal';
import { ShadowView } from '../../../helperComponents/ShadowView';
import {
    AnimatedFastImage,
    AnimatedText,
    AnimatedView,
} from '../../../../utils/animations';
import MostIdealStatisticsModal from '../modals/MostIdealStatisticsModal';

type IdealBonusStepProps = {
    onNext?: () => void;
    onBack?: () => void;
    points: number;
    results: QuestionResultToView;
};

export default function IdealBonusStep({
    points,
    onBack,
    onNext,
    results,
}: IdealBonusStepProps) {
    const { currentUserData } = useSignedInContext();
    const imageUri = mbGetMediumImage(results.imageUrl, { asUri: true });

    const userGuess = results.idealResult?.userGuess || 0;
    const surveyResult = results.idealResult?.surveyResult || 0;

    const [modals, setModals] = useState({
        badge: false,
        statistics: false,
        stack: {
            badge: false,
            statistics: false,
        },
    });

    const onDismiss = useCallback(() => {
        setModals((prev) => {
            const newStack = { ...prev.stack };
            if (prev.badge) {
                newStack.badge = true;
            } else if (prev.statistics) {
                newStack.statistics = true;
            }
            return {
                ...prev,
                badge: false,
                statistics: false,
                stack: newStack,
            };
        });
    }, [setModals]);

    const handleNext = useCallback(() => {
        if (!modals.stack.statistics) {
            setModals((prev) => ({
                ...prev,
                statistics: true,
            }));
        } else if (
            !modals.stack.badge &&
            results.idealResult?.badge === BadgeTypes.idealAnswer
        ) {
            setModals((prev) => ({
                ...prev,
                badge: true,
            }));
        } else {
            onNext?.();
        }
    }, [
        modals.stack.badge,
        modals.stack.statistics,
        onNext,
        results.idealResult?.badge,
    ]);

    const resultColors = useMemo(() => {
        if (surveyResult > 50) {
            return ['#0295CD', '#6A2C8C'];
        }
        return ['#6A2C8C', '#0295CD'];
    }, [surveyResult]);

    const userColor = useMemo(() => {
        if (
            results.idealResult?.surveyTruth === IdealQuestionSurveyResult.yes
        ) {
            return '#0295CD';
        }
        return '#6A2C8C';
    }, [results.idealResult?.surveyTruth]);

    const truth = useMemo(() => {
        if (
            results.idealResult?.surveyTruth === IdealQuestionSurveyResult.yes
        ) {
            return 'Yes';
        } else if (
            results.idealResult?.surveyTruth === IdealQuestionSurveyResult.no
        ) {
            return 'No';
        }
        return '50-50';
    }, [results.idealResult?.surveyTruth]);

    const idealTitleText = useMemo(() => {
        const groupSpecificity = results.idealResult?.groupSpecificity;
        const identity = capitalize(currentUserData?.identity || '');
        const ageGroup = currentUserData?.ageGroup || '';

        if (groupSpecificity === GroupSpecificity.identity) {
            return `${identity} ideal answer to...`;
        } else if (groupSpecificity === GroupSpecificity.ageGroup) {
            return `Sapphics in their ${ageGroup} answer to...`;
        } else if (groupSpecificity === GroupSpecificity.both) {
            return `${identity} in their ${ageGroup} answer to...`;
        }

        // Fallback to identity if groupSpecificity is undefined
        return `${identity} ideal answer to...`;
    }, [results.idealResult?.groupSpecificity, currentUserData?.identity, currentUserData?.ageGroup]);

    return (
        <Layout
            title="IDEAL BONUS"
            titleStyle={styles.title}
            points={points}
            numberOfSteps={getNumberOfSteps(results)}
            currentStep={4}
            onBack={onBack}
            onNext={handleNext}
            nextButtonColor="#FCAF41F4"
            topSectionStyle={styles.topSection}
            titleColor="#FCAF41F4"
            topSectionChildren={
                <View style={styles.topSectionInfo}>
                    <Text style={styles.topSectionTitleText}>
                        {idealTitleText}{'\n'}"
                        <Text style={styles.topSectionTitleItalicText}>
                            {results.sapphicQuestion}"
                        </Text>
                    </Text>
                    <AnimatedFastImage
                        fromScale={0.8}
                        toScale={1}
                        resizeMode={Platform.OS === 'ios' ? 'contain' : 'cover'}
                        style={styles.topSectionImage}
                        source={imageUri}
                    />
                </View>
            }
            bottomSectionStyle={styles.bottomSection}
        >
            {results.answerType === AnswerType.numeric && (
                <Fragment>
                    <SliderResultCard
                        title="Ideal Guess"
                        type={results.answerType}
                        value={userGuess}
                        colors={['#0295CD', '#0295CD']}
                        image={
                            <View style={styles.imageContainer}>
                                <Image
                                    style={styles.image}
                                    source={mbGetMediumImage(
                                        currentUserData?.avatarUrl,
                                        { asUri: true },
                                    )}
                                />
                            </View>
                        }
                        min={results.answerOptions.min}
                        max={results.answerOptions.max}
                    />
                    <Spacer height={15} />
                    <SliderResultCard
                        title="Ideal Truth"
                        type={results.answerType}
                        value={surveyResult}
                        colors={['#6A2C8C', '#6A2C8C']}
                        image={
                            <View style={styles.logoContainer}>
                                <Image
                                    style={styles.logo}
                                    source={imageUtils.screens.logoHomeScreen}
                                    resizeMode={
                                        Platform.OS === 'android'
                                            ? 'stretch'
                                            : 'contain'
                                    }
                                />
                            </View>
                        }
                        min={results.answerOptions.min}
                        max={results.answerOptions.max}
                    />
                </Fragment>
            )}
            {results.answerType === AnswerType.percentage && (
                <Fragment>
                    <View style={styles.statistics}>
                        <AnimatedView
                            fromWidth="50%"
                            toWidth={`${surveyResult}%`}
                            style={styles.statisticsLeftSection}
                        >
                            <Text
                                numberOfLines={1}
                                style={[
                                    styles.statisticsLeftSectionTopText,
                                    { color: resultColors[0] },
                                ]}
                            >
                                Yes
                            </Text>
                            <View
                                style={[
                                    styles.statisticsLeftSectionContainer,
                                    { backgroundColor: resultColors[0] },
                                ]}
                            >
                                <AnimatedText
                                    fromOpacity={0}
                                    toOpacity={1}
                                    adjustsFontSizeToFit
                                    numberOfLines={1}
                                    style={styles.statisticsLeftSectionText}
                                >
                                    {surveyResult}%
                                </AnimatedText>
                            </View>
                        </AnimatedView>
                        <AnimatedView
                            fromWidth="50%"
                            toWidth={`${100 - surveyResult}%`}
                            style={styles.statisticsRightSection}
                        >
                            <Text
                                style={[
                                    styles.statisticsRightSectionTopText,
                                    { color: resultColors[1] },
                                ]}
                            >
                                No
                            </Text>
                            <View
                                style={[
                                    styles.statisticsRightSectionContainer,
                                    { backgroundColor: resultColors[1] },
                                ]}
                            >
                                <AnimatedText
                                    fromOpacity={0}
                                    toOpacity={1}
                                    adjustsFontSizeToFit
                                    numberOfLines={1}
                                    style={styles.statisticsRightSectionText}
                                >
                                    {100 - surveyResult}%
                                </AnimatedText>
                            </View>
                        </AnimatedView>
                    </View>
                    <ShadowView style={styles.resultInfo}>
                        <View style={styles.resultInfoRow}>
                            <View style={styles.resultImageContainer}>
                                <Image
                                    style={styles.image}
                                    source={mbGetMediumImage(
                                        currentUserData?.avatarUrl,
                                        { asUri: true },
                                    )}
                                />
                            </View>
                            <Text
                                style={[
                                    styles.userGuessText,
                                    { color: userColor },
                                ]}
                            >
                                Ideal Guess: {userGuess ? 'Yes' : 'No'}
                            </Text>
                        </View>
                        <Spacer height={15} />
                        <View style={styles.resultInfoRow}>
                            <View
                                style={[
                                    styles.resultImageContainer,
                                    { backgroundColor: '#FFFFFF' },
                                ]}
                            >
                                <Image
                                    style={styles.logo}
                                    source={imageUtils.screens.logoHomeScreen}
                                    resizeMode="contain"
                                />
                            </View>
                            <Text style={[styles.userGuessText]}>
                                Ideal Truth: {truth}
                            </Text>
                        </View>
                    </ShadowView>
                </Fragment>
            )}
            {results.idealResult?.resultType &&
                (results.idealResult.resultType === ResultType.outOfRange ? (
                    <View style={styles.note}>
                        <AnimatedView
                            fromHeight={0}
                            toHeight={58}
                            style={styles.wrongAnswer}
                        >
                            <Text style={styles.answerText}>
                                BETTER LUCK NEXT TIME
                            </Text>
                        </AnimatedView>
                    </View>
                ) : (
                    <View style={styles.note}>
                        <AnimatedView
                            fromHeight={0}
                            toHeight={58}
                            style={styles.correctAnswer}
                        >
                            <Text style={styles.answerText}>
                                {results.idealResult.resultType ===
                                    ResultType.exact &&
                                    `YOU WON +${results.idealResult.bonusPoints} points`}
                                {results.idealResult.resultType ===
                                    ResultType.inMargin && 'WITHIN RANGE!'}
                                {results.idealResult.resultType ===
                                    ResultType.inWiggle && 'CLOSE ENOUGH!'}
                            </Text>
                        </AnimatedView>
                    </View>
                ))}
            <MostIdealBadgeModal
                type={results.answerType}
                answer={userGuess}
                isVisible={modals.badge}
                onDismiss={onDismiss}
            />
            <MostIdealStatisticsModal
                type={results.answerType}
                isVisible={modals.statistics}
                onDismiss={onDismiss}
                question={results.sapphicQuestion}
                answer={results.userAnswer}
                idealPercentage={results.idealResult?.idealForPercentageOfUsers}
            />
        </Layout>
    );
}

const styles = StyleSheet.create({
    title: {
        fontSize: 22,
    },
    topSection: {
        backgroundColor: '#FCAF41F4',
        height: '35%',
    },
    topSectionInfo: {
        paddingVertical: 20,
        paddingHorizontal: 20,
        flex: 1,
    },
    topSectionTitleText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 21,
            fontFamily: FONT_STYLES.Inter_600_Italic,
            fontWeight: '600',
            marginBottom: 20,
        },
    ]),
    topSectionTitleItalicText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 21,
            fontFamily: FONT_STYLES.Inter_600_Italic,
            fontWeight: '600',
            marginBottom: 20,
            fontStyle: 'italic',
        },
    ]),
    topSectionImage: Platform.select({
        default: {
            flex: 1,
            alignSelf: 'stretch',
            borderRadius: 12,
            marginHorizontal: 10,
        },
        android: {
            flex: 1,
            aspectRatio: 2,
            borderRadius: 12,
            marginHorizontal: 10,
            alignSelf: 'center',
        },
    }),
    bottomSection: {
        paddingHorizontal: 30,
        justifyContent: 'flex-start',
        paddingTop: 20,
    },
    imageContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 4,
        borderRadius: 80,
        borderColor: '#FFFFFF',
        width: 63,
        height: 63,
        alignSelf: 'center'
    },
    image: {
        width: 55,
        height: 55,
        borderRadius: 80,
    },
    logoContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 4,
        borderRadius: 80,
        borderColor: 'transparent',
    },
    logo: {
        width: 55,
        height: 55,
    },

    // # statistics
    statistics: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        marginTop: 20,
    },
    statisticsLeftSection: {
        alignItems: 'stretch',
        justifyContent: 'center',
        overflow: 'visible',
    },
    statisticsLeftSectionContainer: {
        borderTopLeftRadius: 22,
        borderBottomLeftRadius: 22,
        backgroundColor: '#0295CD',
        height: 55,
        alignItems: 'center',
        justifyContent: 'center',
    },
    statisticsLeftSectionText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 22,
            fontWeight: '700',
            color: '#FFFFFF',
        },
    ]),
    statisticsLeftSectionTopText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 17,
            fontWeight: '700',
            color: '#0295CD',
            position: 'absolute',
            top: -25,
            width: 35,
            alignSelf: 'center',
        },
    ]),
    statisticsRightSection: {
        alignItems: 'stretch',
        justifyContent: 'center',
        overflow: 'visible',
    },
    statisticsRightSectionContainer: {
        borderTopRightRadius: 22,
        borderBottomRightRadius: 22,
        backgroundColor: '#6A2C8C',
        height: 55,
        alignItems: 'center',
        justifyContent: 'center',
    },
    statisticsRightSectionText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 22,
            fontWeight: '700',
            color: '#FFFFFF',
        },
    ]),
    statisticsRightSectionTopText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 17,
            fontWeight: '700',
            color: '#6A2C8C',
            position: 'absolute',
            top: -25,
            width: 35,
            alignSelf: 'center',
        },
    ]),
    // #! statistics

    resultInfo: {
        marginTop: 15,
        alignSelf: 'stretch',
        padding: 15,
    },
    resultInfoRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    resultImageContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderRadius: 80,
        borderColor: '#FFFFFF',
        marginRight: 10,
    },
    userGuessText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 18,
            fontWeight: '600',
            color: '#0295CD',
        },
    ]),

    note: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 10,
        height: 58,
    },
    wrongAnswer: {
        backgroundColor: '#EE3823',
        alignItems: 'center',
        justifyContent: 'center',
    },
    answerText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 22,
            fontFamily: FONT_STYLES.Tabasco_400,
            fontWeight: '400',
        },
    ]),
    correctAnswer: {
        backgroundColor: '#0295CD',
        alignItems: 'center',
        justifyContent: 'center',
    },
});
